// Motivational-driver gate (UJM Strategic Implication #8).
//
// Returns visibility flags that gamification surfaces (item 9) consult:
//   intrinsic = quiet mastery view (no XP / streaks / badges)
//   extrinsic = full XP / streaks / badges / leaderboard
//   balanced  = the middle path — streak + simple progress, no aggressive
//               points-chasing surfaces
//
// Exactly one flag is true at a time. The gate exists because adding XP /
// badge mechanics to an intrinsic-driven learner is known to backfire
// (over-justification effect) — so anything points-y that runs in the UI
// should pass through this hook first.

import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../services/firebase';
import type {
  CogniPrintProfile,
  MotivationalDriver,
  DimensionScore,
} from '../types/cogniprint';

export interface MotivationalDriverVisibility {
  extrinsic: boolean;
  intrinsic: boolean;
  balanced: boolean;
  /** True when the underlying motivation score is missing — caller should
   *  treat the visibility as a default, not a confident assignment. */
  unknown: boolean;
}

const DEFAULT_BALANCED: MotivationalDriverVisibility = {
  extrinsic: false,
  intrinsic: false,
  balanced: true,
  unknown: true,
};

const EXTRINSIC_THRESHOLD = 60; // performance + social
const INTRINSIC_THRESHOLD = 60; // mastery + curiosity

/**
 * Pure mapping from a MotivationalDriver score to visibility flags.
 * Exposed so non-React consumers (tests, server-side prompt builders)
 * can use the same logic.
 */
export function deriveMotivationalDriverVisibility(
  motivation: DimensionScore<MotivationalDriver> | null | undefined,
): MotivationalDriverVisibility {
  if (!motivation || !motivation.score) return DEFAULT_BALANCED;

  const { mastery, performance, social, curiosity } = motivation.score;
  const extrinsicSum = (performance ?? 0) + (social ?? 0);
  const intrinsicSum = (mastery ?? 0) + (curiosity ?? 0);

  if (extrinsicSum >= EXTRINSIC_THRESHOLD && extrinsicSum > intrinsicSum) {
    return { extrinsic: true, intrinsic: false, balanced: false, unknown: false };
  }
  if (intrinsicSum >= INTRINSIC_THRESHOLD && intrinsicSum > extrinsicSum) {
    return { extrinsic: false, intrinsic: true, balanced: false, unknown: false };
  }
  return { extrinsic: false, intrinsic: false, balanced: true, unknown: false };
}

/**
 * React hook that live-subscribes to cogniprints/{userId} and returns the
 * visibility flags derived from the motivation dimension. While loading, or
 * if the profile / motivation dimension is missing (e.g. quick diagnostic
 * only), returns balanced=true with unknown=true so callers can render a
 * sensible default.
 */
export function useMotivationalDriverVisibility(
  userId: string | null | undefined,
): MotivationalDriverVisibility {
  const [visibility, setVisibility] = useState<MotivationalDriverVisibility>(
    DEFAULT_BALANCED,
  );

  useEffect(() => {
    if (!userId) {
      setVisibility(DEFAULT_BALANCED);
      return;
    }
    const unsub = onSnapshot(doc(db, 'cogniprints', userId), (snap) => {
      if (!snap.exists()) {
        setVisibility(DEFAULT_BALANCED);
        return;
      }
      const profile = snap.data() as CogniPrintProfile;
      setVisibility(deriveMotivationalDriverVisibility(profile.motivation));
    });
    return unsub;
  }, [userId]);

  return visibility;
}
